import { ClassFlag } from "../dom-tools/ClassFlag";
import { ElementAttribute } from "../dom-tools/ElementAttribute";
import { ElementType } from "../dom-tools/ElementType";
import { OnClick, OnMouseDown } from "../dom-tools/EventListeners";
import { PropertyEditor } from "./PropertyEditor";
import { SelectionReference } from "./SelectionReference";

export enum JSONType
{
  STRING,
  NUMBER,
  BOOLEAN,
  ARRAY,
  OBJECT,
  NULL,
  FUNCTION,
  SYMBOL
}

export class JSONTypes
{
  static convertToString( type:JSONType )
  {
    switch ( type )
    {
      case JSONType.STRING: return "STRING";
      case JSONType.NUMBER: return "NUMBER";
      case JSONType.BOOLEAN: return "BOOLEAN";
      case JSONType.ARRAY: return "ARRAY";
      case JSONType.OBJECT: return "OBJECT";
      case JSONType.NULL: return "NULL";
      case JSONType.FUNCTION: return "FUNCTION";
      case JSONType.SYMBOL: return "SYMBOL";
    }

    return null;
  }

  static isValueType( type:JSONType )
  {
    if ( JSONType.STRING === type )
    {
      return true;
    }

    if ( JSONType.NUMBER === type )
    {
      return true;
    }

    if ( JSONType.BOOLEAN === type )
    {
      return true;
    }

    if ( JSONType.NULL === type )
    {
      return true;
    }

    return false;
  }
  static getType( value:any ):JSONType
  {
    if ( Array.isArray( value ) )
    {
      return JSONType.ARRAY;
    }

    let type = typeof value;

    switch ( type )
    {
      case "string":   return JSONType.STRING;
      case "number":   return JSONType.NUMBER;
      case "boolean":  return JSONType.BOOLEAN;
      case "function": return JSONType.FUNCTION;
      case "symbol":   return JSONType.SYMBOL;
    }

    return JSONType.OBJECT;
  }

  static getTypeOf( object:any, key:string ):JSONType
  {
    if ( object === null || object === undefined )
    {
      return JSONType.NULL;
    }

    let value = object[ key ];

    if ( Array.isArray( value ) )
    {
      return JSONType.ARRAY;
    }

    let type = typeof value;

    switch ( type )
    {
      case "string":   return JSONType.STRING;
      case "number":   return JSONType.NUMBER;
      case "boolean":  return JSONType.BOOLEAN;
      case "function": return JSONType.FUNCTION;
      case "symbol":   return JSONType.SYMBOL;
    }

    return JSONType.OBJECT;

  }
}

export class JSONViewer extends PropertyEditor<any,any>
{
  static readonly type = new ElementType( "json--viewer" );
  static readonly jsonRootElement   = new ElementType( "json-root" )
  static readonly jsonObjectElement = new ElementType( "json-object" );
  static readonly jsonArrayElement  = new ElementType( "json-array" );
  static readonly jsonOpenerElement = new ElementType( "json-opener" );
  static readonly jsonChildrenElement  = new ElementType( "json-children" );

  static readonly jsonMemberElement = new ElementType( "json-member" );
  static readonly jsonValueElement  = new ElementType( "json-value" );  
  static readonly jsonKeyElement = new ElementType( "json-key" );

  static readonly typeAttribute   = new ElementAttribute( "type" );
  static readonly openFlag = new ClassFlag( "open" );

  static applyOnDocument()
  {
    JSONViewer.type.forAllInDoc( e => JSONViewer.applyOnElement( e ) )
  }

  static applyOnElement( e:Element )
  {
    console.log( "JSON VIEWER" );
    let scriptObject = ElementType.script.query( e );

    if ( ! scriptObject )
    {
      console.log( "JSON VIEWER NO SCRIPT" );
      return;
    }


    let jsonObject =  JSON.parse( scriptObject.textContent );

    console.log( "JSON VIEWER >>", jsonObject );

    let selectionReference = SelectionReference.from( jsonObject );
    let viewer = new JSONViewer( selectionReference );
    
    viewer._update();

    e.innerHTML = "";
    e.appendChild( viewer.editor );
  } 

  constructor( selection:SelectionReference )
  {
    super( selection );

    this.onRead.addListener( () => this._update() );
  }

  _createEditor()
  {
    let jsonRoot = JSONViewer.jsonRootElement.create();
    return jsonRoot as HTMLElement;
  }

  _update()
  {
    let jsonRoot = this.editor;
    jsonRoot.innerHTML = "";

    let jsonObject = this.selection.values.length === 1 ? 
                     this.selection.values[ 0 ] : this.selection.values;
    
    let type = JSONTypes.getType( jsonObject );

    if ( JSONType.ARRAY === type )
    {
      let jsonArray = jsonObject as any[];

      for ( let i = 0; i < jsonArray.length; i++ ) 
      {
        this._buildJSONTree( jsonRoot, jsonArray, i + "", 0 );        
      }
    }
    else if ( JSONType.OBJECT === type )
    {
      for ( let it in jsonObject )
      {
        if ( jsonObject.hasOwnProperty( it ) )
        {
          this._buildJSONTree( jsonRoot, jsonObject, it, 0 );
        }        
      }      
    }
    else
    {
      let member = JSONViewer.jsonValueElement.create( jsonObject );      
      jsonRoot.appendChild( member );
      JSONViewer.typeAttribute.to( member, JSONTypes.convertToString( type ) );
    }


  }

  _buildJSONTree( parentElement:Element, jsonObject:any, key:string, offset = 0 )
  {
    let type = JSONTypes.getTypeOf( jsonObject, key );

    if ( JSONTypes.isValueType( type ) )
    {
      let member = JSONViewer.jsonMemberElement.create(
        [
          JSONViewer.jsonOpenerElement.create( " " ),
          JSONViewer.jsonKeyElement.create( key ),
          JSONViewer.jsonValueElement.create( jsonObject[ key ] + "" )
        ]
      );
      
      parentElement.appendChild( member );

      JSONViewer.typeAttribute.to( member, JSONTypes.convertToString( type ) );
      
      return;
    }
    
    if ( type === JSONType.OBJECT )
    {
      let objectElement = 
      JSONViewer.jsonObjectElement.create( 
        [
          JSONViewer.jsonOpenerElement.create( "+" ),
          JSONViewer.jsonKeyElement.create( key ),
          JSONViewer.jsonChildrenElement.create()
        ]
      );
      parentElement.appendChild( objectElement );

      let opener = JSONViewer.jsonOpenerElement.query( objectElement ) as HTMLElement;
      let children = JSONViewer.jsonChildrenElement.query( objectElement ) as HTMLElement;

      OnMouseDown.add( opener, 
        ( e ) => 
        { 
          JSONViewer.openFlag.toggle( children );
          opener.innerHTML = JSONViewer.openFlag.in( children ) ? "-" : "+";
          
          e.preventDefault();
          e.stopImmediatePropagation();    
          e.stopPropagation();

          return false;
        }
      )
      
      let value = jsonObject[ key ];
      

      for ( let it in value )
      {
        if ( value.hasOwnProperty( it ) )
        {
          this._buildJSONTree( children, value, it );
        }
      }
    }
    else if ( type === JSONType.ARRAY )
    {
      let arrayElement =JSONViewer.jsonArrayElement.create( 
        [
          JSONViewer.jsonOpenerElement.create( "+" ),
          JSONViewer.jsonKeyElement.create( key ),
          JSONViewer.jsonChildrenElement.create()
        ]
      );

      let opener = JSONViewer.jsonOpenerElement.query( arrayElement ) as HTMLElement;
      let children = JSONViewer.jsonChildrenElement.query( arrayElement ) as HTMLElement;

      OnMouseDown.add( opener, 
        ( e ) => 
        { 
          JSONViewer.openFlag.toggle( children );
          opener.innerHTML = JSONViewer.openFlag.in( children ) ? "-" : "+";
          e.preventDefault();
          e.stopImmediatePropagation();
          e.stopPropagation();

          return false;
        }
      )

      parentElement.appendChild( arrayElement );

      let value = jsonObject[ key ] as any[];
      
      for ( let i = 0; i < value.length; i++ ) 
      {
        this._buildJSONTree( children, value, i + "" );        
      }
    }
    
  }
}